import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuizLayout } from '../components/QuizLayout';
import { useQuizStore } from '../store/quizStore';
import { useReportBySession } from '../lib/useReportBySession';
import { PREFERS_REDUCED_MOTION } from '../lib/motion';
import { track } from '../lib/analytics';

interface Stage {
  label: string;
  ms: number;
}

const STAGES: Stage[] = [
  { label: 'Analyzing your responses', ms: 1400 },
  { label: 'Mapping your strengths and motivations', ms: 1600 },
  { label: 'Cross-referencing 50,000 career paths', ms: 1800 },
  { label: 'Calculating your top 10 matches', ms: 1500 },
  { label: 'Adding salary insights for each match', ms: 1500 },
  { label: 'Building your personality breakdown', ms: 1500 },
  { label: 'Compiling your 90-day action plan', ms: 1400 },
  { label: 'Finalizing your personalized report', ms: 1300 },
];

const TOTAL_MS = STAGES.reduce((s, st) => s + st.ms, 0); // ~12s
const REDUCED_TOTAL_MS = 4_000;

// After the ticker finishes and the button activates, auto-advance to
// /pricing if the user doesn't click. Keeps the funnel moving for users
// who walk away or get distracted.
const AUTO_ADVANCE_DELAY_MS = 5_000;

export function BuildingReport() {
  const navigate = useNavigate();
  const sessionId = useQuizStore((s) => s.sessionId);
  const leadId = useQuizStore((s) => s.leadId);
  const [stageIdx, setStageIdx] = useState(0);
  // tickerDone = the UX progress sequence (8 stages) has played to the end.
  // reportReady = the actual report row exists in the DB.
  // We only advance the user once BOTH are true, so /pricing is guaranteed
  // to have data on first paint instead of a stripped-down empty page.
  const [tickerDone, setTickerDone] = useState(false);
  const { report: reportData, status: reportStatus } = useReportBySession();
  const reportReady = reportData != null;
  const done = tickerDone && reportReady;
  const [autoAdvanceLeft, setAutoAdvanceLeft] = useState<number | null>(null);

  // Fire-and-forget finalize + Claude generation. Both run in parallel; the
  // ticker paces UX, not the API.
  useEffect(() => {
    if (!sessionId) {
      // Dev-only: allow direct visits for visual review without going
      // through the funnel. Tree-shaken in production builds.
      if (import.meta.env.DEV) return;
      navigate('/');
      return;
    }
    track('building_report_viewed');
    void fetch('/api/finalize-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ client_session_id: sessionId }),
    }).catch(() => {});
    // Sync endpoint — writes a stub to the DB within ~100 ms (so /pricing
    // always has data to render) and then fires off the background
    // generator for real Claude data. The hook below picks up whichever
    // version exists (stub first, real later).
    void fetch('/api/reports-generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ client_session_id: sessionId }),
    }).catch(() => {});
  }, [sessionId, navigate]);

  // Stage ticker. Each stage gets its own setTimeout based on the schedule.
  // Reduced-motion compresses the whole sequence to ~4s.
  useEffect(() => {
    const reduced = PREFERS_REDUCED_MOTION();
    const factor = reduced ? REDUCED_TOTAL_MS / TOTAL_MS : 1;
    const timers: number[] = [];
    let cumulative = 0;
    for (let i = 0; i < STAGES.length; i++) {
      cumulative += STAGES[i].ms * factor;
      const t = window.setTimeout(() => {
        setStageIdx(i + 1);
        if (i === STAGES.length - 1) setTickerDone(true);
      }, cumulative);
      timers.push(t);
    }
    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, []);

  // Auto-advance once the ticker finishes. Skipped when there's no session
  // (dev-mode direct visits) so reviewers can inspect the "done" state.
  useEffect(() => {
    if (!done || !sessionId) return;
    const start = Date.now();
    setAutoAdvanceLeft(Math.ceil(AUTO_ADVANCE_DELAY_MS / 1000));
    const tick = window.setInterval(() => {
      const remaining = AUTO_ADVANCE_DELAY_MS - (Date.now() - start);
      if (remaining <= 0) {
        window.clearInterval(tick);
        track('report_ready_auto_advanced');
        navigate('/pricing');
      } else {
        setAutoAdvanceLeft(Math.ceil(remaining / 1000));
      }
    }, 250);
    return () => window.clearInterval(tick);
  }, [done, sessionId, navigate]);

  const handleContinue = () => {
    track('report_ready_clicked');
    navigate('/pricing');
  };

  const completedCount = Math.min(stageIdx, STAGES.length);
  const pct = Math.round((completedCount / STAGES.length) * 100);

  return (
    <QuizLayout showBack={false}>
      <p className="eyebrow mb-3 text-center">Building your report</p>
      <h1 className="text-display-md text-ink mb-3 text-center">
        {done
          ? 'Your report is ready.'
          : tickerDone
            ? 'Almost done — finishing up.'
            : "We're building your career report."}
      </h1>
      <p className="text-ink-soft text-base text-center mb-9 max-w-md mx-auto">
        {done
          ? 'Tap below to unlock your personalized matches, strengths, and action plan.'
          : tickerDone
            ? 'Our AI is putting the final touches on your matches and 90-day plan — usually 10–15 more seconds.'
            : "Sit tight — we're crunching the numbers across 50,000+ career paths."}
      </p>

      {/* progress strip */}
      <div className="mb-7" aria-hidden>
        <div className="h-1.5 w-full bg-canvas-subtle rounded-full overflow-hidden">
          <div
            className="h-full bg-brand rounded-full transition-[width] duration-500 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-ink-soft text-right tabular-nums">{pct}%</p>
      </div>

      {/* stage ticker */}
      <ul className="space-y-2 mb-8" aria-live="polite">
        {STAGES.map((s, i) => {
          const state =
            i < completedCount ? 'done' : i === completedCount ? 'current' : 'pending';
          return (
            <li
              key={s.label}
              className={`flex items-center gap-3 rounded-cta px-4 py-3 transition-all duration-200 ${
                state === 'done'
                  ? 'bg-canvas-subtle text-ink'
                  : state === 'current'
                    ? 'bg-brand-50 border-2 border-brand text-ink'
                    : 'bg-white border-2 border-ink/10 text-ink-light'
              }`}
            >
              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                {state === 'done' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0d9d7c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
                {state === 'current' && (
                  <span className="w-3 h-3 rounded-full bg-brand animate-pulse" />
                )}
                {state === 'pending' && (
                  <span className="w-2 h-2 rounded-full bg-ink-light/40" />
                )}
              </span>
              <span className="flex-1 font-semibold text-sm">{s.label}</span>
              {state === 'current' && (
                <span className="text-xs font-bold uppercase tracking-wider text-brand">working</span>
              )}
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        className="btn-primary"
        disabled={!done}
        onClick={handleContinue}
      >
        {done
          ? 'Get my report →'
          : tickerDone
            ? 'Finalizing…'
            : 'Building your report…'}
      </button>
      {tickerDone && !reportReady && reportStatus === 'missing' && (
        <p className="text-xs text-err text-center mt-3">
          Hmm, our AI is taking longer than usual. Wait a few more seconds — if nothing
          changes, refresh the page and we'll pick up where we left off.
        </p>
      )}

      {done && autoAdvanceLeft != null && autoAdvanceLeft > 0 && (
        <p className="text-xs text-ink-soft text-center mt-3">
          Redirecting to your report in {autoAdvanceLeft}s…
        </p>
      )}

      {leadId && !done && (
        <p className="text-xs text-ink-soft text-center mt-5">
          We're sending a copy to your inbox too — keep an eye out.
        </p>
      )}
    </QuizLayout>
  );
}
